import React from 'react';
import { connect } from 'react-redux';
import {
  Dialog,
  IconButton,
  InputLabel,
  LinearProgress,
  MenuItem,
  Select,
  SelectProps,
} from '@mui/material';
import {
  Delete as DeleteIcon, CheckCircleOutline, Close,
} from '@mui/icons-material';
import { IAppState } from '../../../state/reducers';
import {
  IDispatchProps, IOwnProps, IProps, IState, IStateProps,
} from './interfaces';
import { SelectOptionType } from '../../../utils/selection';
import {
  cancelJob,
  closeJobStatusReview,
  deleteCompletedJobs,
  JobTab,
  IJobData,
  queryJobs,
  retryAssetJob,
  toggleRemoveJobs,
  toggleStatusFilter,
  updateJobTab,
  updateSortingOrder,
} from '../../../state/jobStatus';
import { ActionStatus, ActionType, IAssetStatus } from '../../../api/vault/assetStatus/interfaces';
import { DialogHeader, DialogHeaderTitle } from '../styles/dialog';
import {
  Actions,
  ButtonsContainer,
  ClearCompletedAssetsButton,
  DeleteQueuedAssetsButton,
  ProgressContainer,
  StatusFilterContainer,
  StyledTab as Tab,
  StyledTable,
  StyledTableContainer,
  StyledTabs as Tabs,
  TabLabelContainer,
  StyledError,
  FilterContainer,
  DatePickerStyled,
} from './styles';
import { statusToText } from './statusToTextMap';
import RemoveJobs from './RemoveJobs/component';
import JobListRefreshTimer from './JobListRefreshTimer';
import JobListTableBody from './JobListTableBody';
import { selectHasManageLiveClippingPermission, selectIsUserVaultSuperAdmin } from '../../../state/user/userOrgRole';
import { VAULT_VIDEO_TYPE } from '../../../constants';
import NumericalCircle from '../NumericalCircle';
import { updateStatusFilter as updateLiveEventsStatusFilter, updateSortingOrder as updateLiveEventsSortingOrder } from '../../../state/liveEvents/jobsLiveEvents';
import JobListTableHeader from './JobListTableHeader';
import { ILiveStreamJob } from '../../../api/vault/livestream';
import { StreamStatus } from '../../../enums';
import LiveEventModals from '../../LiveEvents/LiveEventsModals/component'; // Update the path to the correct location

const selectProps: SelectProps = {
  MenuProps: {
    anchorOrigin: {
      vertical: 'bottom',
      horizontal: 'center',
    },
  },
};

const jobStatusOptions: Array<SelectOptionType<string>> = [
  { label: statusToText[ActionStatus.Queued], value: ActionStatus.Queued },
  { label: statusToText[ActionStatus.InProgress], value: ActionStatus.InProgress },
  { label: statusToText[ActionStatus.Transcoding], value: ActionStatus.Transcoding },
  { label: statusToText[ActionStatus.Completed], value: ActionStatus.Completed },
  { label: statusToText[ActionStatus.Failed], value: ActionStatus.Failed },
];

const liveEventsStatusOptions: Array<SelectOptionType<string>> = [
  { label: StreamStatus.Complete, value: StreamStatus.Complete },
  { label: StreamStatus.Live, value: StreamStatus.Live },
  { label: StreamStatus.Error, value: StreamStatus.Error },
  { label: StreamStatus.Initializing, value: StreamStatus.Initializing },
  { label: StreamStatus.Processing, value: StreamStatus.Processing },
  { label: StreamStatus.Restarting, value: StreamStatus.Restarting },
  { label: StreamStatus.Scheduled, value: StreamStatus.Scheduled },
];

const getLiveEvents = (liveEvents: ILiveStreamJob[], streamStatus: StreamStatus[]): ILiveStreamJob[] => (
  liveEvents ? liveEvents.filter((le) => streamStatus.includes(le._source.streamStatus)) : []
);

const bulkJobTabs = [JobTab.Upload, JobTab.Download, JobTab.Create, JobTab.Publish];

class JobList extends React.Component<IProps, IState> {
  state: IState = {
    liveEventsDateFilter: new Date(new Date().toDateString()),
  };

  static defaultProps: IOwnProps = { isExchange: false };

  renderActions = () => {
    const {
      isExchange,
      toggleRemoveJobs,
      deleteCompletedJobs,
      jobsStatusFilter,
      toggleStatusFilter,
      jobTab,
      // jobData,
      currentJobData,
      isVaultSuperAdmin,
      vaultLiveEventsTab,
      exchangeLiveEventsTab,
      updateLiveEventsStatusFilter,
    } = this.props;

    // Default to downloads
    let clearCompletedJobsText = 'Clear Completed Downloads';
    let deleteQueuedJobsText = 'Delete Queued Downloads';
    const setValues = (clearCompleted: string, deleteQueued: string) => {
      clearCompletedJobsText = clearCompleted;
      deleteQueuedJobsText = deleteQueued;
    };
    if (jobTab === JobTab.Create) {
      setValues('Clear Completed Creates', 'Delete Queued Creates');
    } else if (jobTab === JobTab.Publish) {
      setValues('Clear Completed Publishes', 'Delete Queued Publishes');
    } else if (jobTab === JobTab.Upload) {
      setValues('Clear Completed Uploads', 'Delete Queued Uploads');
    }

    const disableDeleteCompletedJobsButton = currentJobData.completedIds.length === 0;
    const disableDeleteQueuedJobsButton = currentJobData.queued.length === 0;
    const videoType = isExchange ? [VAULT_VIDEO_TYPE.Exchange] : [VAULT_VIDEO_TYPE.Clip, 'clips', VAULT_VIDEO_TYPE.MyVault, VAULT_VIDEO_TYPE.Vault];
    const iconProps = { fontSize: 16, marginRight: '5px' };
    // Live Events
    const isLiveEvents = jobTab === JobTab.LiveEvents || jobTab === JobTab.ExchangeEvents;
    const liveEventsFilter = jobTab === JobTab.LiveEvents ? vaultLiveEventsTab.statusFilter : exchangeLiveEventsTab.statusFilter;
    const statusOptions = isLiveEvents ? liveEventsStatusOptions : jobStatusOptions;
    const statusFilter = isLiveEvents ? liveEventsFilter : jobsStatusFilter;
    return (
      <Actions>
        <FilterContainer>
          <StatusFilterContainer>
            <InputLabel id="statusLabel">Status</InputLabel>
            <Select
              {...selectProps}
              labelId="statusLabel"
              onChange={(e: any) => (isLiveEvents ? updateLiveEventsStatusFilter(e.target.value) : toggleStatusFilter(e.target.value))}
              value={statusFilter}
            >
              <MenuItem value="All">
                <em>All</em>
              </MenuItem>
              {statusOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
              ))}
            </Select>
          </StatusFilterContainer>
          {isLiveEvents && (
            <StatusFilterContainer>
              <InputLabel id="datepickerLabel">Date</InputLabel>
              <DatePickerStyled
                format="MM-dd-yyyy"
                value={this.state.liveEventsDateFilter}
                onChange={(e) => {
                  const dateValid = (e) ? new Date(new Date(e as string | number).toDateString()) : null;
                  if (dateValid && dateValid.toString() !== 'Invalid Date') this.setState({ liveEventsDateFilter: dateValid });
                }}
                data-auto="FilterDateInput"
                slotProps={{
                  actionBar: {
                    actions: ['today'],
                  },
                }}
              />
            </StatusFilterContainer>
          )}
        </FilterContainer>
        {bulkJobTabs.includes(jobTab) && (
          <ButtonsContainer>
            <ClearCompletedAssetsButton
              variant="plainOnPaper"
              onClick={() => deleteCompletedJobs(currentJobData.completedIds, videoType, isVaultSuperAdmin)}
              disabled={disableDeleteCompletedJobsButton}
            >
              <CheckCircleOutline sx={iconProps} />{clearCompletedJobsText}
            </ClearCompletedAssetsButton>
            <DeleteQueuedAssetsButton
              variant="primaryOnWhite"
              onClick={toggleRemoveJobs}
              disabled={disableDeleteQueuedJobsButton}
            >
              <DeleteIcon sx={iconProps} />{deleteQueuedJobsText}
            </DeleteQueuedAssetsButton>
          </ButtonsContainer>
        )}
      </Actions>
    );
  };

  renderTabs = () => {
    const {
      jobTab, updateJobTab, isExchange, hasManageLiveClippingPermission, currentJobData,
      jobData, isVaultSuperAdmin, vaultLiveEventsTab, exchangeLiveEventsTab,
    } = this.props;

    const renderTab = (tab: JobTab, className: string = '') => {
      let label: string;
      let totalActiveJobs = 0;
      let totalFailedJobs = 0;
      const setValues = (text: string, active: number, failed: number) => {
        label = text;
        totalActiveJobs = active;
        totalFailedJobs = failed;
      };
      if (tab === JobTab.Upload) {
        setValues('Uploads', jobData.uploads.active.length, jobData.uploads.failed.length);
      } else if (tab === JobTab.Download) {
        setValues('Downloads', jobData.downloads.active.length, jobData.downloads.failed.length);
      } else if (tab === JobTab.Create) {
        setValues('Create', jobData.creates.active.length, jobData.creates.failed.length);
      } else if (tab === JobTab.Publish) {
        setValues('Publish', jobData.publishes.active.length, jobData.publishes.failed.length);
      } else if (tab === JobTab.LiveEvents) {
        setValues('Live Events', vaultLiveEventsTab.active.length, vaultLiveEventsTab.failed.length);
      } else if (tab === JobTab.ExchangeEvents) {
        setValues('Exchange Events', exchangeLiveEventsTab.active.length, exchangeLiveEventsTab.failed.length);
      } else if (tab === JobTab.ExchangeCreate) {
        setValues('Exchange Create', jobData.exchangeCreates.active.length, jobData.exchangeCreates.failed.length);
      }
      return (
        <Tab
          key={tab}
          label={(
            <TabLabelContainer>
              <StyledError className={totalFailedJobs > 0 ? 'active' : ''} />
              <span className="label">{label}</span>
              <NumericalCircle
                color="white"
                backgroundColor="black"
                value={totalActiveJobs}
                size={18}
                fontSize={10}
              />
            </TabLabelContainer>
          )}
          value={tab}
          className={className}
        />
      );
    };

    const tabs = [JobTab.Upload, JobTab.Download];
    if (!isExchange && hasManageLiveClippingPermission) tabs.push(JobTab.LiveEvents);
    if (!isExchange) tabs.push(JobTab.Create, JobTab.Publish);
    if (isVaultSuperAdmin) tabs.push(JobTab.ExchangeEvents, JobTab.ExchangeCreate);

    return (
      <Tabs
        value={!jobTab ? ActionType.Upload : jobTab}
        onChange={(e, value) => updateJobTab(value)}
        centered
        variant="fullWidth"
      >
        {tabs.map((tab, index) => {
          let classname = '';
          if (index === 0) classname = 'first';
          else if (index === tabs.length - 1) classname = 'last';
          return renderTab(tab, classname);
        })}
      </Tabs>
    );
  };

  render() {
    const {
      isExchange,
      isLoading,
      isLiveEventsLoading,
      isRemoveJobsOpen,
      jobs,
      jobTab,
      currentJobData,
      queryJobs,
      toggleRemoveJobs,
      cancelJob,
      retryAssetJob,
      isRetryDisabled,
      isVaultSuperAdmin,
      closeJobStatusReview,
      order,
      orderBy,
      updateSortingOrder,
      hasManageLiveClippingPermission,
      securityProfilesMapping,
      // Live Events
      updateLiveEventsSortingOrder,
      vaultLiveEventsTab,
      exchangeLiveEventsTab,
    } = this.props;

    const hasLiveEventsAccess = !isExchange && hasManageLiveClippingPermission;
    const videoType = isExchange ? [VAULT_VIDEO_TYPE.Exchange] : [VAULT_VIDEO_TYPE.Clip, 'clips', VAULT_VIDEO_TYPE.MyVault, VAULT_VIDEO_TYPE.Vault];

    const { order: liveEventsOrder, orderBy: liveEventsOrderBy, liveEvents } = jobTab === JobTab.ExchangeEvents
      ? exchangeLiveEventsTab
      : vaultLiveEventsTab;
    return (
      <>
        <Dialog
          open={isRemoveJobsOpen}
          onClose={toggleRemoveJobs}
          maxWidth="sm"
          fullWidth
        >
          <RemoveJobs jobs={currentJobData.queued} />
        </Dialog>
        <DialogHeader>
          <DialogHeaderTitle
            data-auto="JobStatusModal"
          >
            Review Job Status
            <JobListRefreshTimer
              onQueryJobs={(value) => {
                queryJobs(videoType, value, isVaultSuperAdmin, hasLiveEventsAccess ? { date: this.state.liveEventsDateFilter } : null);
              }}
              jobTab={jobTab}
              liveEventsDateFilter={this.state.liveEventsDateFilter}
            />
          </DialogHeaderTitle>
          <IconButton
            edge="start"
            color="inherit"
            onClick={() => closeJobStatusReview(isVaultSuperAdmin)}
            aria-label="close"
          >
            <Close />
          </IconButton>
        </DialogHeader>
        <ProgressContainer>
          {(jobTab === JobTab.LiveEvents || jobTab === JobTab.ExchangeEvents ? isLiveEventsLoading : isLoading) && <LinearProgress />}
        </ProgressContainer>
        {this.renderActions()}
        {this.renderTabs()}
        <StyledTableContainer>
          <StyledTable>
            <JobListTableHeader
              isExchange={isExchange}
              order={order}
              orderBy={orderBy}
              updateSortingOrder={updateSortingOrder}
              jobTab={jobTab}
              updateLiveEventsSortingOrder={updateLiveEventsSortingOrder}
              liveEventsOrder={liveEventsOrder}
              liveEventsOrderBy={liveEventsOrderBy}
            />
            <JobListTableBody
              liveEvents={liveEvents}
              jobs={jobs}
              jobTab={jobTab}
              isExchange={isExchange}
              onJobCancel={(jobId) => cancelJob(jobId)}
              onRetryAssetJob={(assetId, actionType, jobId, assetName) => retryAssetJob(assetId, actionType, isVaultSuperAdmin, jobId, assetName)}
              isRetryDisabled={isRetryDisabled}
              securityProfilesMapping={securityProfilesMapping}
              isSuperAdmin={isVaultSuperAdmin}
            />
          </StyledTable>
        </StyledTableContainer>
        <LiveEventModals />
      </>
    );
  }
}

const mapStateToProps = (state: IAppState): IStateProps => ({
  jobs: state.jobStatus.filteredJobs,
  jobData: state.jobStatus.jobData,
  currentJobData: state.jobStatus.currentJobData,
  isLoading: state.jobStatus.isLoading,
  isRemoveJobsOpen: state.jobStatus.isRemoveJobsOpen,
  order: state.jobStatus.order,
  orderBy: state.jobStatus.orderBy,
  jobsStatusFilter: state.jobStatus?.statusFilter,
  jobTab: state.jobStatus?.jobTab,
  isRetryDisabled: state.jobStatus?.isRetryDisabled ?? false,
  isVaultSuperAdmin: selectIsUserVaultSuperAdmin(state),
  hasManageLiveClippingPermission: selectHasManageLiveClippingPermission(state),
  securityProfilesMapping: state.securityProfiles?.idValueMapping ?? [],
  // Live Events
  isLiveEventsLoading: state.jobsLiveEvents?.isLoading ?? false,
  vaultLiveEventsTab: state.jobsLiveEvents?.vault,
  exchangeLiveEventsTab: state.jobsLiveEvents?.exchange,
});

const mapDispatchToProps: IDispatchProps = {
  queryJobs,
  cancelJob,
  updateSortingOrder,
  toggleStatusFilter,
  toggleRemoveJobs,
  deleteCompletedJobs,
  updateJobTab,
  retryAssetJob,
  closeJobStatusReview,
  // Live Events
  updateLiveEventsStatusFilter,
  updateLiveEventsSortingOrder,
};

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(JobList);
